import { useCallback, useEffect, useState } from 'react';
import {
  chat, type Category, type ChatDay, type ChatRoom as Room,
  type RoomState, type Site, directory,
} from '../lib/api.ts';
import { ChatRoom } from './ChatRoom.tsx';

/**
 * Call Connect: the console that stays open with every store.
 *
 * There is no "start a conversation" step. A store is either one you
 * can talk to or one you cannot, and making someone open a room before
 * typing turned every question into two actions -- which, on a console
 * that is meant to be always-on, read as the product being asleep.
 *
 * Today is live; earlier days are transcripts. The split is the trading
 * day, not midnight, so an overnight shift stays in one piece.
 */
export const Assistant = ({ categories, siteId }: {
  readonly categories: readonly Category[];
  /** Chosen in the sidebar, which is the whole navigation. */
  readonly siteId: string;
}) => {
  const [sites, setSites] = useState<Site[]>([]);
  const [state, setState] = useState<RoomState | null>(null);
  const [dayList, setDayList] = useState<ChatDay[]>([]);
  /** Empty means today. A date means read that day's transcript. */
  const [day, setDay] = useState('');
  const [past, setPast] = useState<Room | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const general = categories.find((c) => c.slug === 'general')
    ?? categories[0];

  useEffect(() => {
    directory.sites().then(setSites).catch(() => setSites([]));
  }, []);

  /**
   * Open today's room for the chosen store.
   *
   * POST /chat/rooms both creates and resumes, and rolls the trading
   * day if the open room belongs to an earlier one, so calling it on
   * every store switch is the whole of the "always open" behaviour.
   */
  const enter = useCallback(async () => {
    if (!siteId || !general) return;
    setBusy(true);
    setError('');
    try {
      setState(await chat.open(general.id, siteId));
    } catch {
      setState(null);
      setError('Could not open the conversation with this store.');
    } finally {
      setBusy(false);
    }
  }, [siteId, general?.id]);

  // Switching store resets to today. Landing on yesterday because that
  // is what was open for the previous store would be a quiet way to
  // reply into an archive.
  useEffect(() => {
    setDay('');
    setPast(null);
    setState(null);
    if (!siteId) return;
    void enter();
    chat.days(siteId)
      .then((r) => setDayList(r.days))
      .catch(() => setDayList([]));
  }, [siteId, enter]);

  // A past day is fetched as a session, not as loose messages: the
  // transcript only makes sense as the conversation it was.
  useEffect(() => {
    if (!siteId || !day) {
      setPast(null);
      return;
    }
    setError('');
    chat.history({ siteId, day })
      .then((rs) => setPast(rs[0] ?? null))
      .catch(() => setError('Could not read that day.'));
  }, [siteId, day]);

  const site = sites.find((s) => s.id === siteId);
  const today = dayList.find((d) => d.live)?.day ?? null;

  if (!siteId) {
    return (
      <div className="chatPane assistantPane">
        <p className="empty">Pick a store to talk to.</p>
      </div>
    );
  }

  return (
    <div className="chatPane assistantPane">
      <div className="roomBar">
        <b>{site?.name ?? 'Store'}</b>

        <label className="fieldLab" htmlFor="ccDay">Day</label>
        <select
          id="ccDay"
          value={day}
          onChange={(e) => setDay(e.target.value)}
        >
          <option value="">Today</option>
          {dayList
            .filter((d) => d.day !== today)
            .map((d) => (
              <option key={d.day} value={d.day}>
                {d.day} ({d.messages})
              </option>
            ))}
        </select>
        {day && <span className="hint">Archive &mdash; read only</span>}
      </div>

      {error && <p className="err">{error}</p>}

      {day ? (
        past ? (
          /* key per session, so changing day cannot leave the previous
             day's messages on screen: ChatRoom's mount fetch returns
             early when it already has some. */
          <ChatRoom
            key={past.id}
            sessionId={past.id}
            initial={[]}
            staffPresent={[]}
            readOnly
          />
        ) : (
          <p className="empty">Nothing was said on {day}.</p>
        )
      ) : state ? (
        <ChatRoom
          key={state.sessionId}
          sessionId={state.sessionId}
          initial={state.messages}
          staffPresent={state.staffPresent}
        />
      ) : (
        <p className="empty">
          {busy ? 'Opening…' : 'No conversation yet.'}
        </p>
      )}
    </div>
  );
};
